import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { STREAMING_SECTION_KEYS } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { SMLContainer } from '../../smartLinks/smlContainer/SMLContainer';
import { SMLPageHead } from '../components/SMLPageHead/SMLPageHead';
import { SMLRouteNames } from '../../../routing/sml/SMLRoutes';
import { smlNavigationGroupMessages, smlNavigationMessages } from '../navigation/smlNavigationMessages';
import { InsightsTabRow } from '../insights/InsightsTabRow';
import { PageTabsInBody } from '../components/SMLPageHead/PageTabsInBody';
import { insightsMessages } from '../insights/insightsMessages';
import { useStreamingPeriod } from './hooks/useStreamingPeriod';
import { useStreamingOverview } from './hooks/useStreamingOverview';
import { useSourceFilter } from './hooks/useSourceFilter';
import { useStreamingNavigate } from './hooks/useStreamingNavigate';
import { CATALOG_PATH, StreamingSection } from './streamingPaths';
import { isSectionAvailable, showsSourcesNote } from './streamingSources';
import { StreamingPeriodPicker } from './parts/StreamingPeriodPicker';
import { StreamingPeriodDates } from './parts/StreamingPeriodDates';
import { StreamingSectionChips } from './parts/StreamingSectionChips';
import { StreamingSourcePills } from './parts/StreamingSourcePills';
import { StreamingStateLines } from './parts/StreamingStateLines';
import { StreamingNoSource } from './StreamingNoSource';
import { StreamingSectionBody } from './StreamingSectionBody';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamingScreenProps {
  accountId: number;
  section: StreamingSection;
  /** The track of `/streaming/tracks/:trackId`. */
  trackKey?: string;
}

/**
 * Insights → Streaming (design your-intelligence a1 "Scoreboard", b1, c to f). The head is the
 * Insights head: one title, the period picker, then the tabs Streaming · Social · Fans above the
 * section chips (design navigation-v2 b 2a). The screen only shows numbers: every connect,
 * Reconnect and "Choose artist" is a link to Catalog (owner decision 2026-10-05).
 */
const StreamingScreenComponent: React.FC<StreamingScreenProps> = ({ accountId, section, trackKey }) => {
  const intl = useIntl();
  const period = useStreamingPeriod();
  const { overview, isLoading, error, retry } = useStreamingOverview(accountId, period.days);
  const filter = useSourceFilter();
  const navigate = useStreamingNavigate();
  const openCatalog = useCallback(() => navigate(CATALOG_PATH), [navigate]);
  const hasSources = (overview?.sources.length ?? 0) > 0;
  const emptySections = useMemo(
    () => (overview === undefined ? [] : STREAMING_SECTION_KEYS.filter((key) => !isSectionAvailable(overview, key))),
    [overview]
  );
  const lockedSections = useMemo(
    () =>
      overview === undefined ? [] : STREAMING_SECTION_KEYS.filter((key) => overview.sections?.[key]?.locked === 'plan'),
    [overview]
  );

  const breadcrumbs = useMemo(
    () => [
      { name: intl.formatMessage(smlNavigationGroupMessages.yoursName) },
      { name: intl.formatMessage(insightsMessages.title) },
      { name: intl.formatMessage(smlNavigationMessages.streaming) },
    ],
    [intl]
  );

  return (
    <SMLContainer
      header={
        <SMLPageHead
          breadcrumbs={breadcrumbs}
          title={intl.formatMessage(insightsMessages.title)}
          subtitle={intl.formatMessage(insightsMessages.subtitle)}
          tabs={<InsightsTabRow screen={SMLRouteNames.streaming} />}
        >
          {overview !== undefined && hasSources ? (
            <div className={styles.headTools}>
              <StreamingPeriodPicker
                days={period.days}
                historyDaysLimit={period.historyDaysLimit}
                onChange={period.setDays}
              />
              <StreamingPeriodDates range={overview.range} previousRange={overview.previousRange} />
            </div>
          ) : undefined}
        </SMLPageHead>
      }
    >
      <PageTabsInBody>
        <div className="mb-3">
          <InsightsTabRow screen={SMLRouteNames.streaming} />
        </div>
      </PageTabsInBody>
      {isLoading ? <LoadingView /> : null}
      {!isLoading && error !== undefined ? <ErrorView error={error} retry={retry} /> : null}
      {!isLoading && error === undefined && overview !== undefined && !hasSources ? (
        <StreamingNoSource onOpenCatalog={openCatalog} />
      ) : null}
      {!isLoading && error === undefined && overview !== undefined && hasSources ? (
        <div className={styles.page}>
          <div className={styles.toolbar}>
            <StreamingSectionChips
              section={section}
              days={period.days}
              emptySections={emptySections}
              earningsHidden={overview.earnings.hidden}
              lockedSections={lockedSections}
              onOpen={navigate}
            />
            <StreamingSourcePills sources={overview.sources} off={filter.off} onToggle={filter.toggle} />
          </div>
          <StreamingStateLines
            sources={overview.sources}
            showSourcesNote={
              (section === 'overview' || section === 'stores' || section === 'countries') && showsSourcesNote(overview)
            }
            onOpenCatalog={openCatalog}
          />
          <StreamingSectionBody
            section={section}
            accountId={accountId}
            overview={overview}
            off={filter.off}
            trackKey={trackKey}
            onNavigate={navigate}
            onOpenCatalog={openCatalog}
          />
        </div>
      ) : null}
    </SMLContainer>
  );
};

export const StreamingScreen = TypedMemo(StreamingScreenComponent);
